// SPDX-License-Identifier: AGPL-3.0-or-later

import {getMuteDurationOptions} from '@app/features/channel/components/MuteOptions';
import type {Channel} from '@app/features/channel/models/Channel';
import {ContextMenuCloseProvider} from '@app/features/ui/action_menu/ContextMenu';
import {MuteIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import itemStyles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import menuItemStyles from '@app/features/ui/action_menu/MenuItem.module.css';
import {MenuItemRadio} from '@app/features/ui/action_menu/MenuItemRadio';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as UserGuildSettingsCommands from '@app/features/user/commands/UserGuildSettingsCommands';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import {getMutedText, getNotificationSettingsLabel} from '@app/lib/overlay/contextMenu';
import {MessageNotifications} from '@app/vendor/constants/NotificationConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

interface Props {
	channel: Channel;
	onClose: () => void;
}
export const ChannelNotificationSettingsDropdown: React.FC<Props> = observer(({channel, onClose}) => {
	const {t, i18n} = useLingui();
	const muteDurations = useMemo(() => getMuteDurationOptions(i18n), [i18n]);
	const guildId = channel.guildId;
	const isGuildChannel = guildId != null;
	const channelOverride = UserGuildSettings.getChannelOverride(guildId ?? null, channel.id);
	const isMuted = channelOverride?.muted ?? false;
	const muteConfig = channelOverride?.mute_config;
	const mutedText = getMutedText(isMuted, muteConfig);
	const channelNotifications = channelOverride?.message_notifications;
	const currentNotificationLevel =
		channelNotifications ?? (isGuildChannel ? MessageNotifications.INHERIT : MessageNotifications.ALL_MESSAGES);
	const channelUnreadBadges = channelOverride?.unread_badges;
	const currentUnreadBadgesLevel =
		channelUnreadBadges == null
			? isGuildChannel
				? MessageNotifications.INHERIT
				: MessageNotifications.ALL_MESSAGES
			: channelUnreadBadges;
	const guildNotificationLevel = guildId
		? UserGuildSettings.getGuildMessageNotifications(guildId)
		: MessageNotifications.ALL_MESSAGES;
	const categoryId = channel.parentId;
	const categoryOverride = guildId ? UserGuildSettings.getChannelOverride(guildId, categoryId ?? '') : null;
	const categoryNotifications = categoryId ? categoryOverride?.message_notifications : undefined;
	const resolveEffectiveLevel = (level: number | undefined, fallback: number): number => {
		if (level === undefined || level === MessageNotifications.INHERIT) {
			return fallback;
		}
		return level;
	};
	const effectiveDefaultLevel = resolveEffectiveLevel(categoryNotifications, guildNotificationLevel);
	const hasCategory = categoryId != null;
	const handleMute = useCallback(
		(duration: number | null) => {
			const muteConfigValue = duration
				? {
						selected_time_window: duration,
						end_time: new Date(Date.now() + duration).toISOString(),
					}
				: null;
			UserGuildSettingsCommands.updateChannelOverride(
				guildId ?? null,
				channel.id,
				{
					muted: true,
					mute_config: muteConfigValue,
				},
				{persistImmediately: true},
			);
			onClose();
		},
		[guildId, channel.id, onClose],
	);
	const handleUnmute = useCallback(() => {
		UserGuildSettingsCommands.updateChannelOverride(
			guildId ?? null,
			channel.id,
			{
				muted: false,
				mute_config: null,
			},
			{persistImmediately: true},
		);
		onClose();
	}, [guildId, channel.id, onClose]);
	const handleUnreadBadgesChange = useCallback(
		(level: number) => {
			const value = level === MessageNotifications.INHERIT ? null : level;
			UserGuildSettingsCommands.updateUnreadBadgesLevel(guildId ?? null, value, channel.id, {
				persistImmediately: true,
			});
		},
		[guildId, channel.id],
	);
	const handleNotificationLevelChange = useCallback(
		(level: number) => {
			if (level === MessageNotifications.INHERIT) {
				UserGuildSettingsCommands.updateChannelOverride(
					guildId ?? null,
					channel.id,
					{
						message_notifications: MessageNotifications.INHERIT,
					},
					{persistImmediately: true},
				);
			} else if (guildId) {
				UserGuildSettingsCommands.updateMessageNotifications(guildId, level, channel.id, {
					persistImmediately: true,
				});
			} else {
				UserGuildSettingsCommands.updateChannelOverride(
					null,
					channel.id,
					{
						message_notifications: level,
					},
					{persistImmediately: true},
				);
			}
		},
		[guildId, channel.id],
	);
	const defaultLabelParts = useMemo(
		() => ({
			main: hasCategory ? t`Use Category Default` : t`Use Community Default`,
			sub: getNotificationSettingsLabel(effectiveDefaultLevel) ?? null,
		}),
		[effectiveDefaultLevel, hasCategory],
	);
	return (
		<ContextMenuCloseProvider value={onClose}>
			<MenuGroup>
				{isMuted ? (
					<MenuItem icon={<MuteIcon />} onClick={handleUnmute} hint={mutedText ?? undefined}>
						{t`Unmute Channel`}
					</MenuItem>
				) : (
					<MenuItemSubmenu
						label={t`Mute Channel`}
						onTriggerSelect={() => handleMute(null)}
						render={() => (
							<MenuGroup>
								{muteDurations.map((duration) => (
									<MenuItem key={duration.value ?? 'until'} onClick={() => handleMute(duration.value)}>
										{duration.label}
									</MenuItem>
								))}
							</MenuGroup>
						)}
					/>
				)}
			</MenuGroup>
			{isGuildChannel && (
				<MenuGroup>
					<MenuItemRadio
						selected={currentNotificationLevel === MessageNotifications.INHERIT}
						onSelect={() => handleNotificationLevelChange(MessageNotifications.INHERIT)}
					>
						<div className={itemStyles.flexColumn}>
							<span>{defaultLabelParts.main}</span>
							{defaultLabelParts.sub && <div className={menuItemStyles.subtext}>{defaultLabelParts.sub}</div>}
						</div>
					</MenuItemRadio>
				</MenuGroup>
			)}
			<MenuGroup>
				<MenuItemRadio
					selected={currentNotificationLevel === MessageNotifications.ALL_MESSAGES}
					onSelect={() => handleNotificationLevelChange(MessageNotifications.ALL_MESSAGES)}
				>
					{t`All Messages`}
				</MenuItemRadio>
				<MenuItemRadio
					selected={currentNotificationLevel === MessageNotifications.ONLY_MENTIONS}
					onSelect={() => handleNotificationLevelChange(MessageNotifications.ONLY_MENTIONS)}
				>
					{t`Only Mentions`}
				</MenuItemRadio>
				<MenuItemRadio
					selected={currentNotificationLevel === MessageNotifications.NO_MESSAGES}
					onSelect={() => handleNotificationLevelChange(MessageNotifications.NO_MESSAGES)}
				>
					{t`Nothing`}
				</MenuItemRadio>
			</MenuGroup>
			<MenuGroup>
				<MenuItemSubmenu
					label={t`Unread Badges`}
					hint={getNotificationSettingsLabel(currentUnreadBadgesLevel) ?? undefined}
					render={() => (
						<MenuGroup>
							{isGuildChannel && (
								<MenuItemRadio
									selected={currentUnreadBadgesLevel === MessageNotifications.INHERIT}
									onSelect={() => handleUnreadBadgesChange(MessageNotifications.INHERIT)}
								>
									{hasCategory ? t`Use Category Default` : t`Use Community Default`}
								</MenuItemRadio>
							)}
							<MenuItemRadio
								selected={currentUnreadBadgesLevel === MessageNotifications.ALL_MESSAGES}
								onSelect={() => handleUnreadBadgesChange(MessageNotifications.ALL_MESSAGES)}
							>
								{t`All Messages`}
							</MenuItemRadio>
							<MenuItemRadio
								selected={currentUnreadBadgesLevel === MessageNotifications.ONLY_MENTIONS}
								onSelect={() => handleUnreadBadgesChange(MessageNotifications.ONLY_MENTIONS)}
							>
								{t`Only Mentions`}
							</MenuItemRadio>
							<MenuItemRadio
								selected={currentUnreadBadgesLevel === MessageNotifications.NO_MESSAGES}
								onSelect={() => handleUnreadBadgesChange(MessageNotifications.NO_MESSAGES)}
							>
								{t`Nothing`}
							</MenuItemRadio>
						</MenuGroup>
					)}
				/>
			</MenuGroup>
		</ContextMenuCloseProvider>
	);
});
